import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MailCheck, RefreshCw, LogOut, CheckCircle2 } from "@/components/icons";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AuthCard, PrimaryButton } from "./Login";

/**
 * VerifyYourEmail — estilo minimalista "Files Management".
 * Fondo blanco full-screen. Paleta blanco / gris #F2F3F5 / negro.
 *
 * Strict gate shown to newly-registered doctors right after sign-up.
 */
export default function VerifyYourEmail() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!user) {
      nav("/login", { replace: true });
      return;
    }
    if (user.email_verified) {
      nav(user.role === "doctor" ? "/onboarding" : "/", { replace: true });
    }
  }, [user, nav]);

  useEffect(() => {
    if (!user || user.email_verified) return;
    const id = setInterval(async () => {
      const fresh = await refresh();
      if (fresh?.email_verified) {
        clearInterval(id);
        toast.success("¡Email verificado! Continuemos con la configuración.");
        nav(fresh.role === "doctor" ? "/onboarding" : "/", { replace: true });
      }
    }, 6000);
    return () => clearInterval(id);
  }, [user, refresh, nav]);

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success(`Correo enviado a ${user.email}`);
    } catch (e) {
      toast.error(
        formatErr(e.response?.data?.detail) ||
          "No pudimos enviar el correo. Inténtalo de nuevo."
      );
    } finally {
      setSending(false);
    }
  };

  const recheck = async () => {
    const fresh = await refresh();
    if (fresh?.email_verified) {
      toast.success("¡Email verificado!");
      nav(fresh.role === "doctor" ? "/onboarding" : "/", { replace: true });
    } else {
      toast.info("Aún no detectamos la verificación. Revisa tu bandeja.");
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen w-full bg-white flex items-center justify-center p-4 sm:p-6">
      <AuthCard>
        <div
          data-testid="verify-your-email-card"
          className="text-center"
        >
          <div className="w-12 h-12 mx-auto rounded-full bg-[#F2F3F5] flex items-center justify-center">
            <MailCheck className="w-6 h-6 text-black" strokeWidth={1.8} />
          </div>
          <h1 className="mt-6 text-2xl sm:text-[28px] font-semibold text-black tracking-tight">
            Verifica tu correo
          </h1>
          <p className="mt-3 text-sm text-black/60 leading-relaxed">
            Te enviamos un enlace de verificación a{" "}
            <strong className="text-black">{user.email}</strong>. Haz clic en el
            botón del correo para activar tu cuenta y continuar con la
            configuración.
          </p>

          <div className="mt-6 bg-[#F2F3F5] rounded-xl p-4 text-left">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-black/50 mb-2">
              Después de verificar podrás:
            </div>
            <ul className="space-y-2 text-sm text-black/70">
              {[
                "Crear tu primera oficina",
                "Invitar a tu secretaria",
                "Empezar a gestionar pacientes y citas",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-black shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 space-y-2">
            <PrimaryButton
              type="button"
              onClick={recheck}
              testId="recheck-verification-btn"
            >
              Ya verifiqué mi correo
            </PrimaryButton>
            <button
              type="button"
              onClick={resend}
              disabled={sending}
              data-testid="resend-verification-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-white text-black text-[15px] font-semibold border border-black/10 hover:bg-[#F7F8F9] disabled:opacity-60 transition"
            >
              <RefreshCw className={`w-4 h-4 ${sending ? "animate-spin" : ""}`} />
              {sending ? "Enviando..." : "Reenviar correo"}
            </button>
          </div>

          <button
            type="button"
            onClick={() => {
              logout();
              nav("/login", { replace: true });
            }}
            data-testid="verify-logout-btn"
            className="mt-6 inline-flex items-center gap-1.5 text-xs font-medium text-black/60 hover:text-black transition"
          >
            <LogOut className="w-3.5 h-3.5" /> Cerrar sesión
          </button>

          <p className="mt-5 text-xs text-black/45 leading-relaxed">
            ¿No te llegó? Revisa tu carpeta de spam o reenvíalo. La página se
            actualizará automáticamente cuando verifiques.
          </p>
        </div>
      </AuthCard>
    </div>
  );
}
